// RFC 9457 Problem Detail — detail may be string or object
export interface ApiError {
  type: string
  title: string
  status: number
  detail: string | Record<string, unknown>
  remedy?: string
  // X-Required-Tier response header on a tier 403 (e.g. 'super_admin').
  requiredTier?: string
  // Stable machine-readable code every REST problem carries (symone-v2 openapi.yaml
  // ProblemDetail.code): unauthorized, forbidden, connector_denied, not_found,
  // conflict, bad_request, connection_required, rate_limited, quota_exceeded,
  // upstream_error, service_unavailable — or a more specific route code
  // (e.g. skill_md_invalid's per-rule code).
  code?: string
}

// Read the symone_csrf cookie (JS-readable, httponly=false)
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)symone_csrf=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// Module-level single-flight: concurrent 401s share one in-flight refresh,
// preventing a stampede of parallel refresh calls.
let refreshPromise: Promise<void> | null = null

// doRefresh uses bare fetch (not apiFetch) so it never recursively triggers
// the refresh-on-401 logic below.
async function doRefresh(): Promise<void> {
  const csrf = getCsrfToken()
  if (!csrf) throw new Error('No CSRF token for refresh')
  const res = await fetch('/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrf, 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error('Refresh failed')
}

function ensureRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

function toApiError(res: Response, raw: Partial<ApiError>): ApiError {
  // FastAPI wraps a structured problem raised via HTTPException(detail={...}) as
  // {"detail": {type, title, status, detail, ...}}. Lift it so callers can switch on
  // `type` (e.g. self_deactivation, seed_managed). Pydantic 422s (detail = array) and
  // plain string details are left as they are.
  let body: Partial<ApiError> = raw
  const inner = raw.detail
  if (inner && typeof inner === 'object' && !Array.isArray(inner) && typeof inner.type === 'string') {
    body = inner as Partial<ApiError>
  }
  const tier = res.headers?.get?.('X-Required-Tier') ?? undefined
  return {
    type: body.type ?? 'about:blank',
    title: body.title ?? res.statusText,
    status: res.status,
    detail: body.detail ?? res.statusText,
    remedy: body.remedy,
    requiredTier: tier || undefined,
    // The problem's own `code` (lifted from `detail`) wins; else the top-level one
    // (route errors with a string detail, middleware 401/403 bodies).
    code:
      typeof body.code === 'string'
        ? body.code
        : typeof raw.code === 'string'
          ? raw.code
          : undefined,
  }
}

async function throwFromResponse(res: Response): Promise<never> {
  let body: Partial<ApiError> = {}
  try {
    body = await res.json()
  } catch {
    // Non-JSON error body — use defaults
  }
  throw toApiError(res, body)
}

// Core fetch wrapper.
// retried=true is the loop guard: a request carrying it skips the refresh logic
// so a second consecutive 401 surfaces as an auth failure rather than looping.
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)

  headers.set('Content-Type', 'application/json')
  if (MUTATION_METHODS.has(method)) {
    const csrf = getCsrfToken()
    if (csrf) headers.set('X-CSRF-Token', csrf)
  }

  const res = await fetch(path, { ...init, method, headers, credentials: 'include' })

  // Refresh-once on any 401 (GETs + mutations). The single-flight ensureRefresh()
  // ensures concurrent 401s share one refresh call. The `retried` flag guards
  // against an infinite loop: if the retry itself returns 401, we throw immediately.
  if (res.status === 401 && !retried) {
    try {
      await ensureRefresh()
    } catch {
      // Refresh failed (e.g. missing symone_refresh cookie) — surface auth failure.
      return throwFromResponse(res)
    }
    // Retry the original request once. Pass retried=true so a second 401 throws.
    return apiFetch<T>(path, init, true)
  }

  if (!res.ok) {
    return throwFromResponse(res)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}
